import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Dumbbell, Moon, Scale, Trash2, Check, Sparkles } from "lucide-react";
import { format, startOfWeek, addDays, addWeeks, isSameDay } from "date-fns";
import { id as idLocale, enUS } from "date-fns/locale";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { SectionHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/dates";

const TYPE_META = {
  workout: { icon: Dumbbell, cls: "bg-primary/15 text-primary border-primary/20", dot: "bg-primary" },
  rest: { icon: Moon, cls: "bg-muted text-muted-foreground border-border", dot: "bg-muted-foreground" },
  bodycomp: { icon: Scale, cls: "bg-sky-500/15 text-sky-500 border-sky-500/20", dot: "bg-sky-500" },
};
const iso = (d) => format(d, "yyyy-MM-dd");

export default function Calendar() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const locale = lang === "id" ? idLocale : enUS;
  const [weekOffset, setWeekOffset] = useState(0);
  const [dialogDate, setDialogDate] = useState(null);
  const [form, setForm] = useState({ type: "workout", title: "" });

  const monday = startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 });
  const days = [...Array(7)].map((_, i) => addDays(monday, i));
  const rangeStart = iso(monday);
  const rangeEnd = iso(addDays(monday, 6));

  const { data: entries } = useQuery({
    queryKey: ["schedule", rangeStart],
    queryFn: async () => (await api.get("/schedule", { params: { start: rangeStart, end: rangeEnd } })).data,
  });

  const byDay = (d) => (entries || []).filter((e) => e.date === iso(d));

  const openAdd = (d) => { setDialogDate(iso(d)); setForm({ type: "workout", title: "" }); };
  const save = async () => {
    await api.post("/schedule", { date: dialogDate, type: form.type, title: form.title || defaultTitle(form.type, t) });
    toast.success(t("save"));
    qc.invalidateQueries({ queryKey: ["schedule", rangeStart] });
    setDialogDate(null);
  };
  const remove = async (id) => { await api.delete(`/schedule/${id}`); qc.invalidateQueries({ queryKey: ["schedule", rangeStart] }); };
  const toggleDone = async (e) => { await api.put(`/schedule/${e.id}`, { completed: !e.completed }); qc.invalidateQueries({ queryKey: ["schedule", rangeStart] }); };
  const applyProgram = async (template) => {
    if (!window.confirm(t("applyProgramConfirm"))) return;
    await api.post("/schedule/apply-template", { week_start: rangeStart, template });
    toast.success(t("chooseProgram") + " ✓");
    qc.invalidateQueries({ queryKey: ["schedule", rangeStart] });
  };

  return (
    <div className="space-y-6">
      <SectionHeader title={t("calendar")}
        action={
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="default" size="sm" className="gap-1" data-testid="choose-program"><Sparkles className="h-4 w-4" /> {t("chooseProgram")}</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => applyProgram("brosplit")} data-testid="prog-brosplit">{t("progBroSplit")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => applyProgram("beginner")} data-testid="prog-beginner">{t("progBeginner")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => applyProgram("fullbody3")} data-testid="prog-fullbody">{t("progFullbody")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => applyProgram("upperlower")} data-testid="prog-upperlower">{t("progUpperLower")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="icon" onClick={() => setWeekOffset(weekOffset - 1)} data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setWeekOffset(0)} data-testid="week-today">{t("thisWeek")}</Button>
            <Button variant="outline" size="icon" onClick={() => setWeekOffset(weekOffset + 1)} data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        } />

      <div className="text-sm text-muted-foreground">
        {format(monday, "d MMM", { locale })} – {format(addDays(monday, 6), "d MMM yyyy", { locale })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs">
        {Object.entries(TYPE_META).map(([k, m]) => (
          <span key={k} className="flex items-center gap-1.5"><span className={cn("h-2.5 w-2.5 rounded-full", m.dot)} /> {t(k === "workout" ? "planned" : k === "rest" ? "restDay" : "bodyComp")}</span>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
        {days.map((d) => {
          const isToday = isSameDay(d, new Date());
          const items = byDay(d);
          return (
            <div key={iso(d)} className={cn("flex flex-col rounded-2xl border bg-card p-3 lg:min-h-40", isToday && "border-primary/50 ring-1 ring-primary/20")} data-testid={`day-${iso(d)}`}>
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase text-muted-foreground">{format(d, "EEE", { locale })}</div>
                  <div className={cn("font-display text-lg font-bold tabular-nums", isToday && "text-primary")}>{format(d, "d")}</div>
                </div>
                <button onClick={() => openAdd(d)} aria-label={t("addSchedule")} className="grid h-10 w-10 place-items-center rounded-lg border text-muted-foreground lg:h-7 lg:w-7 transition-colors hover:bg-accent hover:text-foreground" data-testid={`add-${iso(d)}`}>
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 space-y-1.5">
                {items.map((e) => {
                  const m = TYPE_META[e.type] || TYPE_META.workout;
                  const Icon = m.icon;
                  return (
                    <div key={e.id} className={cn("group rounded-lg border px-2 py-1.5 text-xs", m.cls, e.completed && "opacity-50")} data-testid={`entry-${e.id}`} title={e.note || e.title}>
                      <div className="flex items-center gap-1.5">
                        <Icon className="h-3 w-3 shrink-0" />
                        <span className={cn("truncate font-medium", e.completed && "line-through")}>{e.title}</span>
                      </div>
                      {e.note && <div className="mt-0.5 line-clamp-2 text-[10px] leading-tight opacity-70">{e.note}</div>}
                      <div className="mt-1 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 [@media(hover:none)]:opacity-100">
                        <button onClick={() => toggleDone(e)} className="grid h-9 w-9 place-items-center rounded-md hover:text-foreground lg:h-5 lg:w-5" title={t("markDone")} aria-label={t("markDone")} data-testid={`entry-done-${e.id}`}><Check className="h-4 w-4 lg:h-3 lg:w-3" /></button>
                        <button onClick={() => remove(e.id)} className="grid h-9 w-9 place-items-center rounded-md hover:text-destructive lg:h-5 lg:w-5" aria-label="Delete" data-testid={`entry-delete-${e.id}`}><Trash2 className="h-4 w-4 lg:h-3 lg:w-3" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!entries?.length && (
        <div className="rounded-2xl border border-dashed py-10 text-center text-sm text-muted-foreground">{t("noSchedule")}</div>
      )}

      <Dialog open={!!dialogDate} onOpenChange={(o) => !o && setDialogDate(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("addSchedule")}{dialogDate && ` · ${formatDate(dialogDate, lang)}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("scheduleType")}</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="schedule-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="workout">{t("planned")}</SelectItem>
                  <SelectItem value="rest">{t("restDay")}</SelectItem>
                  <SelectItem value="bodycomp">{t("bodyComp")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("scheduleTitle")}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder={defaultTitle(form.type, t)} data-testid="schedule-title" />
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-schedule">{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function defaultTitle(type, t) {
  if (type === "rest") return t("restDay");
  if (type === "bodycomp") return t("bodyComp");
  return t("train");
}
